import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import Layout from "@/components/Layout";
import Splash from "@/pages/Splash";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Fencing from "@/pages/Fencing";
import Interlocking from "@/pages/Interlocking";
import Decking from "@/pages/Decking";
import Concrete from "@/pages/Concrete";
import Landscaping from "@/pages/Landscaping";
import Renovations from "@/pages/Renovations";
import Masonry from "@/pages/Masonry";
import Gallery from "@/pages/Gallery";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import ServiceArea from "@/pages/ServiceArea";
import Privacy from "@/pages/Privacy";
import Reviews from "@/pages/Reviews";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import CommercialHome from "@/pages/CommercialHome";
import CommercialServices from "@/pages/CommercialServices";
import CommercialServiceDetail from "@/pages/CommercialServiceDetail";
import CommercialContact from "@/pages/CommercialContact";
import IndustrialHome from "@/pages/IndustrialHome";
import IndustrialServices from "@/pages/IndustrialServices";
import IndustrialServiceDetail from "@/pages/IndustrialServiceDetail";
import IndustrialContact from "@/pages/IndustrialContact";
import Admin from "@/pages/Admin";
import NotFound from "@/pages/NotFound";

// Residential canonical paths are the existing top-level URLs (preserves SEO
// equity — see REBUILD_PLAN.md §3). These redirects accept the symmetric
// `/residential/*` form and forward to the canonical residential URL.
const ResidentialServiceRedirect = () => {
    const { service } = useParams();
    return <Navigate to={`/services/${service}`} replace />;
};
const ResidentialBlogPostRedirect = () => {
    const { slug } = useParams();
    return <Navigate to={`/blog/${slug}`} replace />;
};

function App() {
    return (
        <div className="App">
            <HelmetProvider>
                <BrowserRouter>
                    <Routes>
                        <Route element={<Layout />}>
                            {/* Entry / splash */}
                            <Route path="/" element={<Splash />} />

                            {/* ---- Residential (canonical top-level paths) ---- */}
                            <Route path="/residential" element={<Home />} />
                            <Route path="/services" element={<Services />} />
                            <Route path="/services/fencing" element={<Fencing />} />
                            <Route path="/services/interlocking" element={<Interlocking />} />
                            <Route path="/services/decking" element={<Decking />} />
                            <Route path="/services/concrete" element={<Concrete />} />
                            <Route path="/services/landscaping" element={<Landscaping />} />
                            <Route path="/services/renovations" element={<Renovations />} />
                            <Route path="/services/masonry" element={<Masonry />} />
                            <Route path="/gallery" element={<Gallery />} />
                            <Route path="/about" element={<About />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/service-area" element={<ServiceArea />} />
                            <Route path="/reviews" element={<Reviews />} />
                            <Route path="/reviews/google" element={<Reviews sourceSlug="google" />} />
                            <Route path="/reviews/homestars" element={<Reviews sourceSlug="homestars" />} />
                            <Route path="/reviews/bark" element={<Reviews sourceSlug="bark" />} />
                            <Route path="/reviews/houzz" element={<Reviews sourceSlug="houzz" />} />
                            <Route path="/reviews/bbb" element={<Reviews sourceSlug="bbb" />} />
                            <Route path="/blog" element={<Blog />} />

                            {/* Legal — track-agnostic, one canonical URL for all three tracks */}
                            <Route path="/privacy" element={<Privacy />} />
                            <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
                            <Route path="/blog/:slug" element={<BlogPost />} />

                            {/* Residential nested aliases → canonical redirects */}
                            <Route path="/residential/services" element={<Navigate to="/services" replace />} />
                            <Route path="/residential/services/:service" element={<ResidentialServiceRedirect />} />
                            <Route path="/residential/gallery" element={<Navigate to="/gallery" replace />} />
                            <Route path="/residential/about" element={<Navigate to="/about" replace />} />
                            <Route path="/residential/contact" element={<Navigate to="/contact" replace />} />
                            <Route path="/residential/service-area" element={<Navigate to="/service-area" replace />} />
                            <Route path="/residential/blog" element={<Navigate to="/blog" replace />} />
                            <Route path="/residential/blog/:slug" element={<ResidentialBlogPostRedirect />} />

                            {/* ---- Commercial ---- */}
                            <Route path="/commercial" element={<CommercialHome />} />
                            <Route path="/commercial/services" element={<CommercialServices />} />
                            <Route path="/commercial/services/:service" element={<CommercialServiceDetail />} />
                            <Route path="/commercial/gallery" element={<Gallery trackKey="commercial" />} />
                            <Route path="/commercial/about" element={<About trackKey="commercial" />} />
                            <Route path="/commercial/service-area" element={<ServiceArea trackKey="commercial" />} />
                            <Route path="/commercial/contact" element={<CommercialContact />} />
                            <Route path="/commercial/blog" element={<Blog trackKey="commercial" />} />
                            <Route path="/commercial/blog/:slug" element={<BlogPost trackKey="commercial" />} />

                            {/* ---- Industrial ---- */}
                            <Route path="/industrial" element={<IndustrialHome />} />
                            <Route path="/industrial/services" element={<IndustrialServices />} />
                            <Route path="/industrial/services/:service" element={<IndustrialServiceDetail />} />
                            <Route path="/industrial/gallery" element={<Gallery trackKey="industrial" />} />
                            <Route path="/industrial/about" element={<About trackKey="industrial" />} />
                            <Route path="/industrial/service-area" element={<ServiceArea trackKey="industrial" />} />
                            <Route path="/industrial/contact" element={<IndustrialContact />} />
                            <Route path="/industrial/blog" element={<Blog trackKey="industrial" />} />
                            <Route path="/industrial/blog/:slug" element={<BlogPost trackKey="industrial" />} />

                            <Route path="/admin" element={<Admin />} />
                            <Route path="*" element={<NotFound />} />
                        </Route>
                    </Routes>
                </BrowserRouter>
            </HelmetProvider>
        </div>
    );
}

export default App;
